import React, { useState, useEffect, useCallback } from "react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import DataTable from "@/components/shared/DataTable";
import SeverityBadge from "@/components/shared/SeverityBadge";
import MapView from "@/components/shared/MapView";
import api, { extractApiError } from "@/lib/api";
import { downloadReport } from "@/lib/downloadReport";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, Download, Loader2 } from "lucide-react";

// Normalize API response array to always be array
const normalizeCollection = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
};

// Map trip data from API response
const mapTrip = (trip) => {
  if (!trip) return null;
  return {
    id: trip.id || trip.trip_id,
    trip_id: trip.id || trip.trip_id,
    vehicle_id: trip.vehicle?.vehicle_id || trip.vehicle?.id || trip.vehicle_id,
    vehicle_name: trip.vehicle?.name || [trip.vehicle?.make, trip.vehicle?.model].filter(Boolean).join(" ") || trip.vehicle_name || "Ambulance",
    vehicle_plate: trip.vehicle?.registration_number || trip.vehicle?.plate || trip.plate || "—",
    driver_id: trip.driver?.user_id || trip.driver?.id || trip.driver_id,
    driver_name: trip.driver?.name || trip.driver_name,
    pickup_location: trip.pickup_location,
    dropoff_location: trip.dropoff_location,
    started_at: trip.started_at,
    ended_at: trip.ended_at,
    status: trip.status,
    summary: {
      avg_speed_kmh: trip.summary?.avg_speed_kmh || trip.avg_speed_kmh,
      max_speed_kmh: trip.summary?.max_speed_kmh || trip.max_speed_kmh,
      total_points: trip.summary?.total_points || trip.total_points,
    },
  };
};

const mapTelemetryPoint = (point) => ({
  lat: Number.parseFloat(point?.lat ?? point?.latitude),
  lon: Number.parseFloat(point?.lon ?? point?.lng ?? point?.longitude),
  speed: Number.parseFloat(point?.speed ?? point?.speed_kmh),
  timestamp: point?.timestamp || point?.recorded_at || point?.time || null,
});

// Format datetime
const formatEventTime = (timestamp) => {
  if (!timestamp) return "-";
  return new Date(timestamp).toLocaleString();
};

// Format duration
const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return "-";
  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffMs = end - start;
  const diffMins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

export default function AmbulanceCompleted() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [meta, setMeta] = useState({});
  
  // Date filtering
  const [fromDate, setFromDate] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0] // Last 30 days
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]); // Today
  
  // Detail dialog
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripDetail, setTripDetail] = useState(null);
  const [telemetry, setTelemetry] = useState([]);
  const [telemetryLoading, setTelemetryLoading] = useState(false);
  const totalPages = Math.max(1, Number(meta?.pages) || Math.ceil((meta?.total || 0) / pageSize) || 1);

  // Fetch trips list
  const fetchTrips = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const fromDt = fromDate ? new Date(fromDate).toISOString() : undefined;
      const toDt = toDate ? new Date(toDate).toISOString() : undefined;
      
      const response = await api.get("/trips", {
        params: {
          status: "COMPLETED",
          from_dt: fromDt,
          to_dt: toDt,
          page,
          page_size: pageSize,
        },
      });

      const trips = normalizeCollection(response.data).map(mapTrip).filter(Boolean);
      setList(trips);
      
      // Store pagination metadata
      if (response.data.meta) {
        setMeta(response.data.meta);
      }
    } catch (err) {
      const { message } = extractApiError(err);
      setError(message || "Failed to load completed trips");
      setList([]);
      toast.error(message || "Failed to load completed trips");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, fromDate, toDate]);

  // Fetch trip detail
  const fetchTripDetail = useCallback(async (tripId) => {
    try {
      const response = await api.get(`/trips/${tripId}`);
      const trip = mapTrip(response.data?.data || response.data);
      setTripDetail(trip);
      return trip;
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to load trip details");
      return null;
    }
  }, []);

  // Fetch telemetry (route points)
  const fetchTelemetry = useCallback(async (tripId) => {
    try {
      setTelemetryLoading(true);
      const response = await api.get(`/trips/${tripId}/telemetry`, {
        params: { page: 1, page_size: 500 },
      });
      const points = normalizeCollection(response.data)
        .map(mapTelemetryPoint)
        .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lon));
      setTelemetry(points);
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to load route data");
    } finally {
      setTelemetryLoading(false);
    }
  }, []);

  // Open trip detail
  const openTripDetail = useCallback(async (trip) => {
    setSelectedTrip(trip);
    const detail = await fetchTripDetail(trip.trip_id);
    if (detail) {
      await fetchTelemetry(trip.trip_id);
    }
  }, [fetchTripDetail, fetchTelemetry]);

  // Initial load
  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  // Download report
  const handleDownload = () => {
    try {
      downloadReport({
        filename: "ambulance_completed_trips",
        title: "Ambulance - Completed Trips Report",
        headers: ["Trip ID", "Vehicle", "Driver", "Pickup", "Dropoff", "Duration", "Avg Speed (km/h)", "Completed At"],
        rows: list.map((t) => [
          t.trip_id,
          t.vehicle_plate,
          t.driver_name,
          t.pickup_location,
          t.dropoff_location,
          formatDuration(t.started_at, t.ended_at),
          t.summary?.avg_speed_kmh || "-",
          formatEventTime(t.ended_at),
        ]),
      });
      toast.success("Report downloaded");
    } catch (err) {
      const { message } = extractApiError(err);
      toast.error(message || "Failed to download report");
    }
  };

  return (
    <div className="space-y-6" data-testid="ambulance-completed">
      <PageHeader
        title="Completed Trips"
        subtitle="Closed patient transports."
        onDownload={handleDownload}
      />

      {/* Date range filter */}
      <SectionCard className="flex flex-col sm:flex-row gap-4 items-end">
        <div className="flex-1">
          <Label className="text-xs font-semibold text-slate-600">From Date</Label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setPage(1);
            }}
            className="mt-1"
          />
        </div>
        <div className="flex-1">
          <Label className="text-xs font-semibold text-slate-600">To Date</Label>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setPage(1);
            }}
            className="mt-1"
          />
        </div>
        <div className="flex-1">
          <Label className="text-xs font-semibold text-slate-600">Page Size</Label>
          <Select value={pageSize.toString()} onValueChange={(v) => {
            setPageSize(Number(v));
            setPage(1);
          }}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </SectionCard>

      {/* Trips table */}
      <SectionCard padded={false}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600">
            <p className="font-semibold">{error}</p>
            <Button onClick={fetchTrips} variant="outline" size="sm" className="mt-4">
              Retry
            </Button>
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <p>No completed trips found</p>
          </div>
        ) : (
          <>
            <DataTable
              data={list}
              columns={[
                // {
                //   key: "trip_id",
                //   label: "Trip ID",
                //   cellClassName: "font-mono text-xs font-semibold text-slate-900",
                //   render: (t) => t.trip_id,
                // },
                {
                  key: "vehicle",
                  label: "Vehicle",
                  render: (t) => `${t.vehicle_plate} - ${t.vehicle_name}`,
                },
                {
                  key: "driver",
                  label: "Driver",
                  render: (t) => t.driver_name,
                },
                {
                  key: "pickup",
                  label: "Pickup Location",
                  render: (t) => t.pickup_location,
                },
                {
                  key: "dropoff",
                  label: "Dropoff Location",
                  render: (t) => t.dropoff_location,
                },
                {
                  key: "duration",
                  label: "Duration",
                  render: (t) => formatDuration(t.started_at, t.ended_at),
                },
                {
                  key: "avg_speed",
                  label: "Avg Speed (km/h)",
                  cellClassName: "text-slate-600",
                  render: (t) => Number.isFinite(Number(t.summary?.avg_speed_kmh)) ? Number(t.summary.avg_speed_kmh).toFixed(2) : "-",
                },
                {
                  key: "completed_at",
                  label: "Completed At",
                  cellClassName: "text-xs text-slate-500",
                  render: (t) => formatEventTime(t.ended_at),
                },
                {
                  key: "actions",
                  label: "Actions",
                  className: "text-right",
                  cellClassName: "text-right",
                  render: (t) => (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openTripDetail(t)}
                      className="gap-2"
                    >
                      <Eye size={14} /> View Route
                    </Button>
                  ),
                },
              ]}
            />
            
            {/* Pagination */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
              <div className="text-sm text-slate-600">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(p - 1, 1))}
                  disabled={page === 1 || loading}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages || loading}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </SectionCard>

      {/* Trip detail dialog with route replay */}
      {selectedTrip && (
        <Dialog open={!!selectedTrip} onOpenChange={(open) => {
          if (!open) {
            setSelectedTrip(null);
            setTripDetail(null);
            setTelemetry([]);
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Trip {selectedTrip.trip_id} - Route Replay</DialogTitle>
            </DialogHeader>

            {tripDetail ? (
              <div className="space-y-6">
                {/* Trip info grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-semibold text-slate-600">Vehicle</p>
                    <p className="font-mono text-sm mt-1">{tripDetail.vehicle_plate}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-600">Driver</p>
                    <p className="text-sm mt-1">{tripDetail.driver_name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-600">Pickup</p>
                    <p className="text-sm mt-1">{tripDetail.pickup_location}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-600">Dropoff</p>
                    <p className="text-sm mt-1">{tripDetail.dropoff_location}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-600">Duration</p>
                    <p className="text-sm mt-1">{formatDuration(tripDetail.started_at, tripDetail.ended_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-600">Avg Speed</p>
                    <p className="text-sm mt-1">{Number.isFinite(Number(tripDetail.summary?.avg_speed_kmh)) ? Number(tripDetail.summary.avg_speed_kmh).toFixed(2) : "-"} km/h</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-600">Max Speed</p>
                    <p className="text-sm mt-1">{Number.isFinite(Number(tripDetail.summary?.max_speed_kmh)) ? Number(tripDetail.summary.max_speed_kmh).toFixed(2) : "-"} km/h</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-600">Started At</p>
                    <p className="text-xs text-slate-600 mt-1">{formatEventTime(tripDetail.started_at)}</p>
                  </div>
                </div>

                {/* Map with telemetry */}
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-2">Route Map</p>
                  {telemetryLoading ? (
                    <div className="flex items-center justify-center h-80 bg-slate-100 rounded-lg">
                      <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                    </div>
                  ) : telemetry.length > 0 ? (
                    <div className="h-80 rounded-lg overflow-hidden border border-slate-200">
                      <MapView
                        incidents={telemetry.map((point, idx) => ({
                          id: idx,
                          latitude: point.lat,
                          longitude: point.lon,
                          timestamp: point.timestamp,
                          speed: point.speed,
                        }))}
                        defaultZoom={14}
                      />
                    </div>
                  ) : (
                    <div className="h-80 bg-slate-100 rounded-lg flex items-center justify-center text-slate-500">
                      No route data available
                    </div>
                  )}
                </div>

                {/* Telemetry points */}
                {telemetry.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-600 mb-2">GPS Points ({telemetry.length})</p>
                    <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="text-left px-3 py-2 font-semibold">Latitude</th>
                            <th className="text-left px-3 py-2 font-semibold">Longitude</th>
                            <th className="text-left px-3 py-2 font-semibold">Speed (km/h)</th>
                            <th className="text-left px-3 py-2 font-semibold">Timestamp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {telemetry.map((point, idx) => (
                            <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50">
                              <td className="px-3 py-2 font-mono">{Number.isFinite(Number(point.lat)) ? Number(point.lat).toFixed(6) : "-"}</td>
                              <td className="px-3 py-2 font-mono">{Number.isFinite(Number(point.lon)) ? Number(point.lon).toFixed(6) : "-"}</td>
                              <td className="px-3 py-2">{Number.isFinite(Number(point.speed)) ? Number(point.speed).toFixed(2) : "-"}</td>
                              <td className="px-3 py-2">{formatEventTime(point.timestamp)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
